"use client";

import { useCallback, useState } from "react";

import { Button } from "@/components/ui";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useUser } from "@/contexts/user-provider";

import type { NoteDetail, NoteRequiredForm } from "@/types/notes";

import { useSignOffNoteMutation } from "@/api/provider/scribe/note-mutation";

const FORM_STATUS_TEXT: Record<NoteRequiredForm["status"], string> = {
  COMPLETED: "completed",
  EXPIRED: "expired",
  IN_PROGRESS: "started, not finished",
  NOT_STARTED: "not started",
};

/** "Botox Consent · not started" — how a missing required form reads in a sentence list. */
export function requiredFormLine(form: NoteRequiredForm): string {
  return `${form.name} · ${FORM_STATUS_TEXT[form.status]}`;
}

/**
 * Sign-off with shared guards: the visit's required forms still missing (when the
 * caller knows them), then "you haven't adopted a signature".
 *
 * Sign-off happens from several places (manual note, AI note, note dropdown); this
 * hook centralizes the warnings so every entry point behaves the same. Call
 * `requestSignOff(noteId, { missingForms })` from a button/menu item, and render
 * `warningDialog` once in the same component. Neither guard blocks: each asks once.
 * `onSigned` gets the note back from the server once the signature lands.
 */
export function useSignOffWithSignatureGuard(options?: {
  onSigned?: (note: NoteDetail) => void;
}) {
  const onSigned = options?.onSigned;
  const { currentUser } = useUser();
  const signOffMutation = useSignOffNoteMutation();
  const [pendingNoteId, setPendingNoteId] = useState<string | null>(null);
  const [pendingForms, setPendingForms] = useState<{
    noteId: string;
    forms: NoteRequiredForm[];
  } | null>(null);

  const signOff = useCallback(
    (noteId: string) => {
      signOffMutation.mutate(
        { noteId },
        { onSuccess: (note) => note && onSigned?.(note) },
      );
    },
    [signOffMutation, onSigned],
  );

  // The signature check, once any forms question has been answered.
  const continueSignOff = useCallback(
    (noteId: string) => {
      if (!currentUser?.signatureImageUrl) {
        setPendingNoteId(noteId);
        return;
      }
      signOff(noteId);
    },
    [currentUser?.signatureImageUrl, signOff],
  );

  const requestSignOff = useCallback(
    (noteId: string, options?: { missingForms?: NoteRequiredForm[] }) => {
      if (options?.missingForms?.length) {
        setPendingForms({ noteId, forms: options.missingForms });
        return;
      }
      continueSignOff(noteId);
    },
    [continueSignOff],
  );

  const formsDialog = (
    <Dialog
      open={pendingForms !== null}
      onOpenChange={(open) => {
        if (!open) setPendingForms(null);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sign off with forms missing?</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          <p className="text-body text-foreground-secondary">
            {pendingForms?.forms.length === 1
              ? "This visit requires a form the patient hasn't completed:"
              : "This visit requires forms the patient hasn't completed:"}
          </p>
          <ul className="flex flex-col gap-1 text-body text-foreground">
            {pendingForms?.forms.map((form) => (
              <li key={form.formId}>{requiredFormLine(form)}</li>
            ))}
          </ul>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setPendingForms(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const noteId = pendingForms?.noteId;
              setPendingForms(null);
              if (noteId) continueSignOff(noteId);
            }}
            disabled={signOffMutation.isPending}
          >
            Sign off anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const signatureDialog = (
    <Dialog
      open={pendingNoteId !== null}
      onOpenChange={(open) => {
        if (!open) setPendingNoteId(null);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sign off without a signature?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-body text-foreground-secondary">
            You haven&apos;t adopted a signature yet, so your sign-off
            won&apos;t stamp one on this note. You can adopt a signature in
            Settings → Profile.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setPendingNoteId(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingNoteId) signOff(pendingNoteId);
              setPendingNoteId(null);
            }}
            disabled={signOffMutation.isPending}
          >
            Sign off anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const warningDialog = (
    <>
      {formsDialog}
      {signatureDialog}
    </>
  );

  return {
    requestSignOff,
    warningDialog,
    isPending: signOffMutation.isPending,
  };
}
